import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import TopStrip from "../components/TopStrip";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import HelpButton from "../components/HelpButton";

import heroBanner from "../assets/images/hero-images/technology partnership.webp";
import heroMobile from "../assets/images/mobile-banners/technology-mobile.webp";
import downloadImg from "../assets/images/webp-images/download-section-img.webp";
import companyPdf from "../assets/pdfs/DDevOps-Company-Profile.pdf";

import efficientImg from "../assets/images/tech-page/Efficient.27f25754.png";
import managementImg from "../assets/images/tech-page/managment2.70107d5d.png";
import multiImg from "../assets/images/tech-page/multi1.7713b5e4.png";
import highlyImg from "../assets/images/tech-page/highly.db1a23e4.png";
import integrationImg from "../assets/images/tech-page/integration.601a5b60.png";
import qualityImg from "../assets/images/tech-page/quality.2269cb8e.png";

import logo1 from "../assets/images/logo-slide/1.avif";
import logo2 from "../assets/images/logo-slide/2.avif";
import logo3 from "../assets/images/logo-slide/3.avif";
import logo4 from "../assets/images/logo-slide/4.avif";
import logo5 from "../assets/images/logo-slide/5.avif";
import logo6 from "../assets/images/logo-slide/6.avif";
import logo7 from "../assets/images/logo-slide/7.avif";
import logo8 from "../assets/images/logo-slide/8.avif";
import logo9 from "../assets/images/logo-slide/9.avif";

const logoImages = [
  logo1,
  logo2,
  logo3,
  logo4,
  logo5,
  logo6,
  logo7,
  logo8,
  logo9,
];

const advantageBlocks = [
  {
    num: 1,
    reverse: false,
    img: efficientImg,
    alt: "Data Security",
    title: "Enterprise-Grade Security",
    desc: "Security isn't an afterthought; it's our foundation. When you partner with us, your application inherits our rigorous Zero-Trust security protocols, ensuring your clients' data remains protected by the highest industry standards without you building it from scratch.",
  },
  {
    num: 2,
    reverse: true,
    img: managementImg,
    alt: "Management Goals",
    title: "Strategic Operational Alignment",
    desc: "We don't just manage servers; we manage outcomes. We align our operational KPIs directly with your business goals, ensuring that our infrastructure support actively drives your product roadmap and helps you meet your SLAs.",
  },
  {
    num: 3,
    reverse: false,
    img: multiImg,
    alt: "Multi Function",
    title: "Full-Cycle Automation",
    desc: "Speed is the currency of the modern web. We automate the entire delivery lifecycle—from code commit to production deployment. This reliability ensures you can release new features to your customers faster and with zero downtime.",
  },
  {
    num: 4,
    reverse: true,
    img: highlyImg,
    alt: "Professional Team",
    title: "Your Extended DevOps Team",
    desc: "Stop hiring expensive in-house operations staff. Partnering with DDevOps instantly adds 40+ certified engineers to your workforce. We handle the heavy lifting of 24/7 server management so your internal team can focus purely on innovation.",
  },
  {
    num: 5,
    reverse: false,
    img: integrationImg,
    alt: "Integration",
    title: "Deep Infrastructure Integration",
    desc: "We bridge the gap between code and metal. Our experts ensure your software is perfectly optimized for the underlying infrastructure, whether it's bare metal, cloud, or hybrid—eliminating compatibility issues for our mutual clients.",
  },
  {
    num: 6,
    reverse: true,
    img: qualityImg,
    alt: "Quality Control",
    title: "Continuous Quality Assurance",
    desc: "We embed quality gates directly into the deployment pipeline. With automated testing and real-time monitoring, we ensure that every release is stable, performant, and bug-free before it ever reaches the end user.",
  },
];

const engineServices = [
  {
    title: "Solution Architecture",
    desc: "We don't just deploy; we design. Our architects work with your team to build high-availability infrastructure blueprints that optimize your software's performance and resilience.",
  },
  {
    title: "24/7 Managed Operations",
    desc: "Your software never sleeps, and neither do we. We provide round-the-clock monitoring and management, ensuring 99.9% uptime for the platforms running your technology.",
  },
  {
    title: "Data Sovereignty & Compliance",
    desc: "Help your clients meet their regulatory needs. We implement rigorous security protocols and backup strategies that align with GDPR, HIPAA, and ISO standards.",
  },
  {
    title: "Cloud Migration Strategy",
    desc: "We facilitate adoption. We help legacy clients migrate seamlessly from on-premise systems to your modern cloud or SaaS platform with zero data loss.",
  },
  {
    title: "Capacity Planning",
    desc: "Scale without surprise costs. We analyze usage patterns and implement auto-scaling strategies to ensure infrastructure grows in perfect sync with user demand.",
  },
  {
    title: "Advanced Threat Defense",
    desc: "Protect your reputation. We deploy multi-layer firewall inspections and intrusion detection systems (IDS) to shield your application from DDoS attacks and malicious traffic.",
  },
];

function TechnologyPartnership() {
  const logoTrackRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    const track = logoTrackRef.current;
    if (!track) return;
    const run = () => {
      posRef.current -= 0.5;
      if (Math.abs(posRef.current) >= track.scrollWidth / 2) posRef.current = 0;
      track.style.left = posRef.current + "px";
      animRef.current = requestAnimationFrame(run);
    };
    animRef.current = requestAnimationFrame(run);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <>
      <TopStrip />
      <Navbar />

      {/* HERO */}
      <section style={{ marginTop: "125px" }}>
        <div className="container-fluid p-0">
          <img
            src={heroBanner}
            width="1600"
            height="520"
            alt="Technology partnership banner with human and robotic hands, digital interface, and innovation tagline."
            className="img-fluid w-100 d-none d-md-block"
          />
          <img
            src={heroMobile}
            width="768"
            height="1000"
            alt="Technology partnership banner with human and robotic hands, digital interface, and innovation tagline."
            className="img-fluid w-100 d-md-none d-block"
          />
        </div>
      </section>

      {/* TECH PARTNER INTRO */}
      <section className="my-5">
        <div className="container">
          <div className="container py-4">
            <h2
              className="text-uppercase fw-bold text-center"
              data-aos="fade-down"
            >
              Technology Partnership for{" "}
              <span className="txt-color">Technology Innovation</span>
            </h2>
          </div>
          <div className="row g-4">
            {/* Left */}
            <div className="col-lg-6 col-md-6 col-sm-12 d-flex align-items-start flex-column justify-content-center">
              <h3 className="text-md-start text-center ps-0 fs-6 fs-md-5">
                Technology Partnership for managed IT services such as IT
                operations, system engineering, server management, cloud
                computing, infrastructure & network management, security
                assistance, etc.
              </h3>
              <div className="d-flex flex-wrap justify-content-md-start justify-content-center gap-3 mt-3">
                <Link
                  to="/contact"
                  className="btn btn-info text-white fw-bold"
                  data-aos="fade-right"
                >
                  Let's Talk About Contract
                </Link>
                <Link
                  to="/contact"
                  className="btn btn-info text-white fw-bold"
                  data-aos="fade-left"
                >
                  Free Consultation
                </Link>
              </div>
            </div>
            {/* Right */}
            <div className="col-lg-6 col-md-6 col-sm-12 text-md-start text-center border-start border-3 border-info">
              <p>
                We help organizations implement and optimize their technical
                systems. Our partnerships typically take place between
                technology vendors who provide the product and the agencies and
                companies who want to enhance their daily operations
                capabilities through our skills employed to deliver the service
                (design, build, run) together. Technically, these fall into
                three main categories: business process services, application
                services, and infrastructure services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY PARTNER — Synergy */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-uppercase">
              Synergy That Drives <span className="text-info">Growth</span>
            </h2>
            <p
              className="text-muted mt-3 mx-auto"
              style={{ maxWidth: "720px" }}
            >
              In today's complex technology landscape, integration is
              everything. By partnering with DDevOps, you gain a strategic ally
              focused on operational excellence.
            </p>
          </div>
          <div className="row g-4">
            <div className="col-lg-4 col-md-6">
              <div className="h-100 bg-white p-4 rounded shadow-sm border text-center">
                <div className="mb-3">
                  <i className="bi bi-patch-check-fill text-info fs-1"></i>
                </div>
                <h5 className="fw-semibold mb-3">Validated Integrations</h5>
                <p className="text-muted">
                  Get your technology <strong>DDevOps Certified</strong>. We
                  validate your tools within our high-performance stacks, giving
                  customers confidence that our solutions work seamlessly
                  together.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="h-100 bg-white p-4 rounded shadow-sm border text-center">
                <div className="mb-3">
                  <i className="bi bi-bar-chart-line-fill text-info fs-1"></i>
                </div>
                <h5 className="fw-semibold mb-3">Market Expansion</h5>
                <p className="text-muted">
                  Gain visibility within our established client base of
                  enterprises and high-growth startups actively seeking robust,
                  managed solutions.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mx-md-auto">
              <div className="h-100 bg-white p-4 rounded shadow-sm border text-center">
                <div className="mb-3">
                  <i className="bi bi-shield-check text-info fs-1"></i>
                </div>
                <h5 className="fw-semibold mb-3">Enhanced Customer Success</h5>
                <p className="text-muted">
                  When your technology is backed by our 24/7 managed operations,
                  customers experience higher uptime and faster ROI—reducing
                  churn and increasing long-term success for both partners.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERSHIP FRAMEWORKS */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="mb-5">
            <h2 className="fw-bold text-uppercase">
              Our <span className="text-info">Partnership</span> Frameworks
            </h2>
            <p className="text-muted mt-2" style={{ maxWidth: "720px" }}>
              We collaborate through clearly defined frameworks that align
              technology, operations, and long-term growth objectives.
            </p>
          </div>

          {/* TYPE 1 */}
          <div className="row align-items-center mb-5">
            <div className="col-lg-3 mb-3 mb-lg-0">
              <div className="p-3 border-start border-4 border-info bg-light h-100">
                <h5 className="fw-bold mb-1">TYPE 1</h5>
                <p className="mb-0 text-muted">
                  Independent Software Vendors (ISVs) & Tool Providers
                </p>
              </div>
            </div>
            <div className="col-lg-9">
              <div className="p-4 border rounded h-100">
                <p className="mb-2">
                  <strong>Who this is for:</strong> Companies like Splunk,
                  Docker, HashiCorp, or new SaaS monitoring/security tools.
                </p>
                <p className="mb-2">
                  <strong>How we collaborate:</strong> We integrate your tools
                  into our standard CI/CD and operational pipelines. We become
                  experts in deploying and managing your software, ensuring our
                  clients get the most out of your features without the
                  operational headache.
                </p>
                <p className="mb-0">
                  <strong>Goal:</strong> Seamless technical integration and
                  "Preferred Tool" status in our stacks.
                </p>
              </div>
            </div>
          </div>

          {/* TYPE 2 */}
          <div className="row align-items-center">
            <div className="col-lg-3 mb-3 mb-lg-0">
              <div className="p-3 border-start border-4 border-info bg-light h-100">
                <h5 className="fw-bold mb-1">TYPE 2</h5>
                <p className="mb-0 text-muted">
                  Cloud & Infrastructure Alliances
                </p>
              </div>
            </div>
            <div className="col-lg-9">
              <div className="p-4 border rounded h-100">
                <p className="mb-2">
                  <strong>Who this is for:</strong> Hyperscalers (AWS, Azure,
                  Google Cloud) and specialized infrastructure providers
                  (DigitalOcean, Vultr).
                </p>
                <p className="mb-2">
                  <strong>How we collaborate:</strong> DDevOps acts as the
                  expert service layer on top of your raw compute. We help
                  clients migrate to your platform and ensure they follow best
                  practices, driving consumption and stickiness on your
                  infrastructure.
                </p>
                <p className="mb-0">
                  <strong>Goal:</strong> Driving adoption of your platform
                  through expert managed services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PATH TO PARTNERSHIP */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-uppercase">
              Path to <span className="text-info">Partnership</span>
            </h2>
            <p
              className="text-muted mt-2"
              style={{ maxWidth: "760px", margin: "0 auto" }}
            >
              A clear, structured approach designed to ensure alignment,
              technical excellence, and long-term success.
            </p>
          </div>
          <div className="row g-4">
            {[
              {
                step: "Step 01",
                title: "Discovery & Alignment",
                desc: "We meet to discuss mutual goals, target markets, and technical synergy.",
              },
              {
                step: "Step 02",
                title: "Technical Validation",
                desc: "Our engineers test integration points to ensure our combined solution meets DDevOps' rigorous standards for stability and security.",
              },
              {
                step: "Step 03",
                title: "Joint Go-To-Market",
                desc: "We launch the partnership through co-branded collateral, inclusion in our service catalog, and internal team training.",
              },
            ].map((s, i) => (
              <div className="col-lg-4" key={i}>
                <div className="h-100 p-4 bg-white border rounded text-center position-relative">
                  <div className="mb-3">
                    <span className="badge bg-info fs-6 px-3 py-2">
                      {s.step}
                    </span>
                  </div>
                  <h5 className="fw-bold mb-2">{s.title}</h5>
                  <p className="text-muted mb-0">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP ADVANTAGE — zigzag blocks */}
      <section className="py-5 px-3 bg-light">
        <div className="container">
          <h2 className="fw-bold text-uppercase text-center">
            The Partnership <span className="text-info">Advantage</span>
          </h2>
          <p className="text-center">
            Why leading technology vendors choose DDevOps as their operational
            backbone.
          </p>

          {advantageBlocks.map((b, i) => (
            <div
              className={`row align-items-center mb-5 ${b.reverse ? "flex-lg-row-reverse" : ""}`}
              key={i}
            >
              <div className="col-lg-6 col-md-6 col-sm-12 mb-4 mb-md-0">
                <div className="p-4 p-lg-5 rounded shadow-sm border-start border-info border-3 position-relative bg-white">
                  <div
                    className="position-absolute top-0 start-0 translate-middle bg-info text-white rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    {b.num}
                  </div>
                  <h5 className="fw-bold mt-3 fs-5">{b.title}</h5>
                  <p className="text-muted mb-3">{b.desc}</p>
                  <Link
                    to="/"
                    className="btn btn-info text-white rounded-circle px-3 py-1 btn-sm"
                  >
                    {">"}
                  </Link>
                </div>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 text-center">
                <img
                  src={b.img}
                  loading="lazy"
                  className="img-fluid"
                  alt={b.alt}
                  style={{ maxHeight: "350px" }}
                  data-aos="zoom-in"
                  data-aos-delay={`${150 + i * 100}`}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DARK CTA 1 */}
      <section className="py-5 bg-dark bg-gradient">
        <div className="container">
          <div className="row align-items-center justify-content-between gy-4">
            <div className="col-lg-8 text-white">
              <h2 className="fw-bold mb-3">
                Let's define the future of IT operations together.
              </h2>
              <p className="fs-5 mb-0 opacity-75">
                Are you ready to build strategic partnerships that drive growth,
                innovation, and operational excellence?
              </p>
            </div>
            <div className="col-lg-4 text-lg-end text-center">
              <Link
                to="/contact"
                className="btn btn-light btn-lg fw-bold px-5 py-3 shadow"
              >
                Start the Conversation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ENGINE BEHIND GROWTH */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="text-uppercase fw-semibold mb-3">
              The Engine Behind Your <span className="text-info">Growth</span>
            </h2>
            <p className="text-muted col-lg-8 mx-auto fs-5">
              Leverage our operational capabilities to deliver a superior
              experience to your customers.
            </p>
          </div>
          <div className="row g-4">
            {engineServices.map((s, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <div className="h-100 bg-white p-4 rounded-4 shadow-sm border">
                  <h5 className="fw-semibold mb-3 text-info">{s.title}</h5>
                  <p className="text-muted small mb-0">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD SECTION */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center mb-5">
            <h4 className="fw-semibold mb-3 top-line">
              Ready to expand your market reach and reliability?
            </h4>
            <Link to="/contact" className="fs-5 fw-bold">
              Join Our Partner Ecosystem
            </Link>
          </div>
          <div
            className="row align-items-center gy-4"
            style={{ marginTop: "5rem" }}
          >
            <div
              className="col-lg-6 col-md-12 text-center text-lg-start"
              data-aos="fade-left"
              data-aos-delay="300"
            >
              <div className="px-3 px-lg-5">
                <p className="text-dark opacity-75 fs-4 mb-2">
                  PARTNERSHIP RESOURCES
                </p>
                <h2 className="fw-bold mb-3">
                  Download Our <br /> Capability Statement
                </h2>
                <p className="text-muted mb-4">
                  Need to share our technical details with your board or product
                  team? Download our comprehensive Corporate Profile to explore
                  our operational standards, security protocols, and global
                  infrastructure footprint.
                </p>
                <a
                  className="btn btn-info text-white fw-bold px-4 py-3 mt-2 d-inline-block"
                  target="_blank"
                  rel="noreferrer"
                  download
                  href={companyPdf}
                >
                  Download Capability Deck (1.5MB)
                </a>
              </div>
            </div>
            <div
              className="col-lg-6 col-md-12 text-center"
              data-aos="fade-right"
              data-aos-delay="300"
            >
              <img
                src={downloadImg}
                loading="lazy"
                alt="Capability Statement"
                className="img-fluid mt-4 floating-icon"
                style={{ maxHeight: "350px", objectFit: "contain" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* DARK CTA 2 */}
      <section className="py-5 bg-dark bg-gradient">
        <div className="container">
          <div className="row align-items-center justify-content-between gy-4">
            <div className="col-lg-8 text-white">
              <h2 className="fw-bold mb-3">
                Ready to build a powerful alliance?
              </h2>
              <p className="fs-5 mb-0 opacity-75">
                Let's discuss how our technologies can align to create unmatched
                value for our mutual clients.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end text-center">
              <Link
                to="/contact"
                className="btn btn-light btn-lg fw-bold px-5 py-3 shadow"
              >
                Schedule a Partner Meeting
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LOGO SLIDER */}
      <section className="logo-slider py-1 bg-white overflow-hidden mt-5">
        <h2 className="text-center text-uppercase top-line">
          The <span className="text-info fw-semibold">DDevOps</span> Toolkit
        </h2>
        <div className="container" style={{ margin: "1rem auto" }}>
          <div className="logo-wrapper">
            <div
              className="logo-track"
              ref={logoTrackRef}
              style={{ position: "relative" }}
            >
              {[...logoImages, ...logoImages].map((img, i) => (
                <img
                  key={i}
                  src={img}
                  loading="lazy"
                  className="slide-logo-img"
                  alt={`Logo ${(i % 9) + 1}`}
                  height="60"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <HelpButton />
    </>
  );
}

export default TechnologyPartnership;
